import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup, sendEmailVerification } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth, googleProvider } from '../../services/firebaseAuth';
import api from 'services/api';
import { PAGES } from '../../constants';
import Alert from 'components/Alert';
import { useSetAtom } from 'jotai';
import { adminAtom, isAuthenticatedAtom } from '../../store/atoms';
import { Button, Paper, Typography } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { IAdmin } from 'common';

export function UserLogin() {
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const setAdmin = useSetAtom(adminAtom);
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.currentUser) {
      navigate('/Users');
    }
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setErrors([]);
      
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      if (!user.emailVerified) {
        try {
          await sendEmailVerification(user);
          setErrors(['Please verify your email address. A verification email has been sent.']);
        } catch (_err: unknown) {
          setErrors(['Could not send verification email. Please try again.']);
        }
        setLoading(false);
        return;
      }

      const idToken = await user.getIdToken();
      
      try {
        await api.firebaseAuth({
          uid: user.uid,
          idToken,
          has_accepted_cgu: true,
        });

        const adminData = await api.getAdmin();
        setAdmin(adminData.admin as IAdmin);
        setIsAuthenticated(true);
        navigate(PAGES.Admin[0]);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Authentication failed';
        setErrors([message]);
        await auth.signOut();
        navigate('/login');
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError && error.code === 'auth/popup-closed-by-user') {
        return;
      }
      const message = error instanceof Error ? error.message : 'Sign in failed';
      setErrors([message]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        width: '100%',
        textAlign: 'center',
        bgcolor: '#323232',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: '#ffffff' }}>
        Login
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: '#909191' }}>
        Sign in with your Google account
      </Typography>

      {errors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          message={error}
          onClose={() => {
            setErrors((prev) => prev.filter((err) => err !== error));
          }}
        />
      ))}

      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<GoogleIcon sx={{ color: '#ffffff' }} />}
        onClick={handleGoogleSignIn}
        disabled={loading}
        sx={{
          mt: 2,
          py: 1.5,
          textTransform: 'none',
          fontSize: '1rem',
          bgcolor: '#0005DC',
          color: '#ffffff',
          '&:hover': {
            bgcolor: '#0004B0',
          },
        }}
      >
        {loading ? 'Signing in...' : 'Sign in with Google'}
      </Button>
    </Paper>
  );
}
 